// AUTO-GENERATED from docs/TECHNICAL_PRD.md — CID Agent PWA API layer
// Module: Contacts Registry (vendors, purchasers, co-agencies + nested bank accounts)
// Every list (collection GET) takes params { page, limit, ...filters }.
import client from './client';

/**
 * GET /api/agent/vendors [LIST · paginated]
 * List vendors/landlords in the caller's own contact book: the ones they registered,
 * plus the parties already on their sales and booking forms. Never another agent's.
 * Params: { page, limit, search, mine } — `mine=1` returns only the ones the caller
 * registered themselves (the same set they can edit). Same flag on purchasers/co-agencies.
 * @disposition ADAPT_SCOPE · maps to GET /api/vendors (vendor.controller.listVendors -> vendor.service.listVendors)
 */
export const listVendors = (params = {}) => client.get(`/api/agent/vendors`, { params });

/**
 * GET /api/agent/vendors/:id
 * Vendor detail incl. bank accounts and only the caller's own linked deals.
 * @disposition ADAPT_SCOPE · maps to GET /api/vendors/:id (vendor.service.getVendorById)
 */
export const getVendorsById = (id, params = {}) => client.get(`/api/agent/vendors/${id}`, { params });

/**
 * POST /api/agent/vendors
 * Create a vendor/landlord as the caller agent, with nested bank accounts, reusable on the agent's future deals/forms.
 * @disposition ADAPT_SCOPE · maps to POST /api/vendors (vendor.controller.createVendor)
 */
export const createVendors = (data = {}) => client.post(`/api/agent/vendors`, data);

/**
 * PUT /api/agent/vendors/:id
 * Edit a vendor the caller created, including replacing its nested bank accounts.
 * @disposition ADAPT_SCOPE · maps to PUT /api/vendors/:id (vendor.controller.updateVendor)
 */
export const updateVendorsById = (id, data = {}) => client.put(`/api/agent/vendors/${id}`, data);

/**
 * GET /api/agent/purchasers [LIST · paginated]
 * List purchasers/tenants in the caller's own contact book (own + on their deals).
 * @disposition ADAPT_SCOPE · maps to GET /api/purchasers (purchaser.controller.listPurchasers)
 */
export const listPurchasers = (params = {}) => client.get(`/api/agent/purchasers`, { params });

/**
 * GET /api/agent/purchasers/:id
 * Purchaser detail incl. bank accounts and only the caller's linked deals.
 * @disposition ADAPT_SCOPE · maps to GET /api/purchasers/:id (purchaser.service.getPurchaserById)
 */
export const getPurchasersById = (id, params = {}) => client.get(`/api/agent/purchasers/${id}`, { params });

/**
 * POST /api/agent/purchasers
 * Create a purchaser/tenant as the caller agent, with nested bank accounts.
 * @disposition ADAPT_SCOPE · maps to POST /api/purchasers (purchaser.controller.createPurchaser)
 */
export const createPurchasers = (data = {}) => client.post(`/api/agent/purchasers`, data);

/**
 * PUT /api/agent/purchasers/:id
 * Edit a purchaser the caller created + its bank accounts.
 * @disposition ADAPT_SCOPE · maps to PUT /api/purchasers/:id (purchaser.controller.updatePurchaser)
 */
export const updatePurchasersById = (id, data = {}) => client.put(`/api/agent/purchasers/${id}`, data);

/**
 * GET /api/agent/co-agencies [LIST · paginated]
 * List co-agencies in the caller's own contact book: the ones they registered, plus the
 * agencies on their own sales and co-agency letters. (This list once had ZERO scoping —
 * every agent saw all 1,921 — which is what the agent scope was added to close.)
 * @disposition ADAPT_SCOPE · maps to GET /api/co-agencies (coAgency.controller.listCoAgencies)
 */
export const listCoAgencies = (params = {}) => client.get(`/api/agent/co-agencies`, { params });

/**
 * GET /api/agent/co-agencies/:id
 * Co-agency detail incl. bank accounts, but only the caller's own letters and deal linkages.
 * @disposition ADAPT_SCOPE · maps to GET /api/co-agencies/:id (coAgency.service.getCoAgencyById)
 */
export const getCoAgenciesById = (id, params = {}) => client.get(`/api/agent/co-agencies/${id}`, { params });

/**
 * POST /api/agent/co-agencies
 * Create a co-agency as the caller agent, with nested bank accounts, reusable on the caller's co-agency letters/deals.
 * @disposition ADAPT_SCOPE · maps to POST /api/co-agencies (coAgency.controller.createCoAgency)
 */
export const createCoAgencies = (data = {}) => client.post(`/api/agent/co-agencies`, data);

/**
 * PUT /api/agent/co-agencies/:id
 * Edit a co-agency the caller created + its bank accounts.
 * @disposition ADAPT_SCOPE · maps to PUT /api/co-agencies/:id (coAgency.controller.updateCoAgency)
 */
export const updateCoAgenciesById = (id, data = {}) => client.put(`/api/agent/co-agencies/${id}`, data);

/**
 * GET /api/agent/contacts/lookup [LIST · paginated]
 * Unified lightweight typeahead/picker across vendors+purchasers+co-agencies for building
 * booking forms / listings / co-agency letters, so the agent reuses existing contacts
 * instead of duplicating. Same scope as the three lists — the caller's own book — plus
 * `include_ids`, which pins already-attached parties onto page 1 even when HQ registered
 * them and the caller could never have found them by searching.
 * @disposition BUILD_NEW
 */
export const listContactsLookup = (params = {}) => client.get(`/api/agent/contacts/lookup`, { params });
